import { DocumentUploadViewModel, DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { SystemDocumentDialogProps } from "./SystemDocumentDialog";
import { useTranslation } from "../../../../localization/useTranslation";
import { useState } from "react";
import { useParams } from "react-router";
import { useValidator } from "../../../../common/hooks/validation";
import { Result } from "../../../../common/api/result";
import { put, sendWithFile } from "../../../../common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { createPlaceholderFile, getFileToUpload } from "@common/documentUploadFile";
import { SystemDocumentSaveModel, SystemDocumentViewModel } from "../SystemDocuments.types";
import { isNullOrWhitespace } from "../../../../common/stringOperations";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useSystemDocumentDialog(props: SystemDocumentDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const isEdit = props.selectedDocument !== undefined;

    const [document, setDocument] = useState<SystemDocumentViewModel>(new SystemDocumentViewModel(props.selectedDocument?.name));
    const [documentUploadModel, setDocumentUploadModel] = useState<DocumentUploadViewModel>(
        new DocumentUploadViewModel(isEdit && !props.selectedDocument?.link ? [createPlaceholderFile()] : [], props.selectedDocument?.link)
    );
    const { id } = useParams<{ id: string }>();
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const validation = useValidator<SystemDocumentViewModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        []
    );

    const editMutation = useDotLegalMutation(edit, {
        onSuccess: (response: Result<string>) => {
            snackbar.show(translateString("documentWasUpdated"));
            props.onDialogClose();
            props.onSave();
        },
    });

    const saveMutation = useDotLegalMutation(create, {
        onSuccess: (response: Result<string>) => {
            snackbar.show(translateString("documentCreated"));
            props.onDialogClose();
            props.onSave();
        },
    });

    function create(saveModel: SystemDocumentSaveModel) {
        return sendWithFile<string>("POST", `/System/${id}/Document`, saveModel, getFileToUpload(documentUploadModel.files), trackUploadProgress());
    }

    function edit(saveModel: SystemDocumentSaveModel) {
        return put<string>(`/System/${props.selectedDocument?.documentId}/Document`, saveModel);
    }

    const save = async () => {
        validation.setShowErrors(true);

        if (validation.isValid && documentUploadModel.IsModelValid()) {
            if (isEdit) {
                await editMutation.mutateAsync(mapToSaveModel());
            } else {
                await saveMutation.mutateAsync(mapToSaveModel());
            }
        }
    };

    const mapToSaveModel = () => {
        const hasFiles = documentUploadModel.files && documentUploadModel.files.length;
        const model: SystemDocumentSaveModel = {
            fileName: hasFiles ? documentUploadModel.files[0].fileName : "",
            name: document.name ?? "",
            link: documentUploadModel.link,
        };

        return model;
    };

    async function onDocumentUpload(files: DotLegalFile[] | undefined) {
        let tempDoc = { ...document! };

        let fileName = files && files!.length > 0 ? files![0].fileName : "";

        if (isNullOrWhitespace(tempDoc.name)) {
            tempDoc.name = fileName.substring(0, fileName.lastIndexOf("."));
        }

        setDocument(tempDoc);
    }

    function onNameChange(value: string) {
        let tempDoc = { ...document };

        tempDoc.name = value;
        setDocument(tempDoc);
    }

    return {
        document,
        save,
        validation,
        isEdit,
        onNameChange,
        isSaving: saveMutation.isLoading || editMutation.isLoading,
        isUploading: saveMutation.isLoading && getFileToUpload(documentUploadModel.files) !== undefined,
        uploadPercentage,
        documentUploadModel,
        setDocumentUploadModel,
        isLink: documentUploadModel.link ? true : false,
        onDocumentUpload,
    };
}
